import { motion } from "framer-motion";
import { Plus, Clock, Star } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useUI } from "@/context/UIContext";
import { localize, formatTRY } from "@/lib/format";
import { getMeta } from "@/data/productMeta";
import { getProductById } from "@/data/menu";
import { PopularBadge, SpicyBadge, VegBadge } from "@/components/Badges";

export function ProductCard({ product, index = 0 }) {
  const { lang, t } = useLang();
  const { openProduct } = useUI();
  const soldOut = !product.available;

  const handleClick = () => {
    if (!soldOut) return openProduct(product.id);
    const alts = getMeta(product.id).altIds || [];
    const alt = alts.map(getProductById).find((p) => p && p.available);
    if (alt) {
      toast(t("soldOutToday"), { description: t("tryInstead") });
      openProduct(alt.id);
    }
  };

  return (
    <motion.button
      type="button"
      onClick={handleClick}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.3) }}
      whileTap={soldOut ? {} : { scale: 0.98 }}
      className={`group flex w-full gap-4 rounded-2xl border border-border bg-card p-3 text-start shadow-soft transition-colors ${
        soldOut ? "opacity-70" : "hover:border-primary/40"
      }`}
      data-testid={`product-card-${product.id}`}
      aria-disabled={soldOut}
    >
      <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-xl bg-muted">
        <img
          src={product.image}
          alt={localize(product.name, lang)}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {soldOut && (
          <div className="absolute inset-0 grid place-items-center bg-black/55">
            <span className="rounded-full bg-white/90 px-2.5 py-1 text-center text-[10px] font-bold leading-tight text-black">
              {t("soldOutToday")}
            </span>
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start gap-2">
          <h4 className="line-clamp-2 flex-1 font-heading text-[15px] font-bold leading-snug">
            {localize(product.name, lang)}
          </h4>
          {product.popular && !soldOut && (
            <Star className="mt-0.5 h-4 w-4 shrink-0 fill-amber-400 text-amber-400" />
          )}
        </div>
        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
          {localize(product.description, lang)}
        </p>

        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {product.spicy && <SpicyBadge />}
          {product.vegetarian && <VegBadge />}
          {product.prepTimeMin >= 15 && (
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <Clock className="h-3 w-3" />
              {product.prepTimeMin} {t("min")}
            </span>
          )}
        </div>

        <div className="mt-auto flex items-center justify-between pt-2">
          <span className="font-heading text-base font-extrabold">{formatTRY(product.basePrice)}</span>
          {!soldOut && (
            <span className="grid h-9 w-9 place-items-center rounded-full brand-gradient text-white shadow-soft transition-transform group-active:scale-90">
              <Plus className="h-5 w-5" strokeWidth={2.5} />
            </span>
          )}
        </div>
      </div>
    </motion.button>
  );
}

// Compact vertical card for "popular" horizontal rails.
export function PopularCard({ product, index = 0 }) {
  const { lang } = useLang();
  const { openProduct } = useUI();
  return (
    <motion.button
      type="button"
      onClick={() => openProduct(product.id)}
      initial={{ opacity: 0, x: 18 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: Math.min(index * 0.06, 0.4) }}
      whileTap={{ scale: 0.97 }}
      className="group relative w-44 shrink-0 overflow-hidden rounded-2xl border border-border bg-card text-start shadow-soft"
      data-testid={`popular-card-${product.id}`}
    >
      <div className="relative h-32 w-full overflow-hidden bg-muted">
        <img
          src={product.image}
          alt={localize(product.name, lang)}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute start-2 top-2">
          <PopularBadge />
        </div>
      </div>
      <div className="p-3">
        <h4 className="line-clamp-1 font-heading text-sm font-bold">{localize(product.name, lang)}</h4>
        <div className="mt-1 flex items-center justify-between">
          <span className="font-heading text-sm font-extrabold">{formatTRY(product.basePrice)}</span>
          <span className="grid h-7 w-7 place-items-center rounded-full brand-gradient text-white">
            <Plus className="h-4 w-4" strokeWidth={2.5} />
          </span>
        </div>
      </div>
    </motion.button>
  );
}
